import { useNavigate } from 'react-router-dom';
import { ContentBox } from 'app/agriculture/components/ContentBox/ContentBox';
import { Actions } from 'app/agriculture/components/Actions/Actions';
import { NoteIcon } from 'app/agriculture/icons';
import { FormProvider, SubmitHandler, useForm } from 'react-hook-form';
import { useNote } from 'app/agriculture/queries/notes/useNote';
import { MapLocation } from 'app/agriculture/queries/types/MapLocation';
import { TextArea } from 'app/agriculture/forms/components/TextArea/TextArea';
import { EntityParentSelectField } from 'app/agriculture/forms/components/EntityParentSelectField/EntityParentSelectField';
import { SpecificLocationField } from 'app/agriculture/forms/components/SpecificLocationField/SpecificLocationField';
import { FormDiscardChangesBlocker } from 'app/agriculture/forms/components/FormDiscardChangesBlocker/FormDiscardChangesBlocker';
import { useEntityUpdateMutation } from 'app/agriculture/mutations/entity/update/useEntityUpdateMutation';
import { useRouteActions } from 'app/agriculture/router/useRouteActions';
import './NoteEditForm.scss';
import { ShowOnMapButton } from 'app/agriculture/components/ContentBox/components/ShowOnMapButton';
import { useAttachOptions } from 'app/agriculture/hooks/useAttachOptions/useAttachOptions';
import { noteAttachOptionTypes } from 'app/agriculture/routes/Notes/utils/noteAttachOptionTypes';
import { convertMapLocationToPosition } from 'app/agriculture/components/utils/convertMapLocationToPosition';

interface NoteEditFields {
    text: string;
    parentId: string | null;
    location: MapLocation | null;
}

export function NoteEditForm({ noteId }: { noteId: string }) {
    const note = useNote(noteId);
    const navigate = useNavigate();
    const routeActions = useRouteActions();

    const formController = useForm<NoteEditFields>({
        defaultValues: {
            text: note.text,
            parentId: note.parentId,
            location: note.location,
        },
        mode: 'all',
        shouldFocusError: true,
    });

    const {
        register,
        reset,
        setValue,
        handleSubmit,
        formState: { errors, isDirty },
    } = formController;

    const { mutate, isPending } = useEntityUpdateMutation('Note');

    const onSubmit: SubmitHandler<NoteEditFields> = (formData) => {
        mutate(
            { id: noteId, ...formData },
            {
                onSuccess: () => {
                    reset({}, { keepValues: true });
                    navigate('../');
                },
                onError: () => {
                    reset({}, { keepValues: true });
                },
            },
        );
    };

    const attachOptions = useAttachOptions(noteAttachOptionTypes);

    return (
        <ContentBox
            title="Edit Note"
            Icon={NoteIcon}
            className="NoteEditForm"
            variant="md"
            showOnMapButton={
                <ShowOnMapButton position={convertMapLocationToPosition(note.location)} />
            }
        >
            <FormProvider {...formController}>
                <form onSubmit={handleSubmit(onSubmit)}>
                    <FormDiscardChangesBlocker />
                    <ContentBox.Section scrollable className="NoteEditForm__fields">
                        <TextArea
                            label="Note"
                            {...register('text', { required: 'Please add a note' })}
                            className="NoteCreate__text-input"
                            errorMessage={errors.text?.message}
                        />
                        <EntityParentSelectField
                            label="Attach Note To"
                            attachOptions={attachOptions}
                        />
                        <SpecificLocationField
                            isPin
                            entityType="Note"
                            initialValue={note.location}
                            onChange={(value) => {
                                setValue('location', value, { shouldDirty: true });
                            }}
                        />
                        <Actions
                            sticky
                            primaryAction={{
                                text: 'Save note changes',
                                type: 'submit',
                                isLoading: isPending,
                                disabled: !isDirty,
                            }}
                            secondaryActions={[
                                {
                                    startIcon: 'delete',
                                    text: 'Delete',
                                    theme: 'danger',
                                    onClick: () => {
                                        routeActions.remove(noteId, { type: 'Note' });
                                    },
                                },
                            ]}
                            backAction={{
                                text: 'Back',
                                onClick: () => {
                                    navigate(-1);
                                },
                            }}
                        />
                    </ContentBox.Section>
                </form>
            </FormProvider>
        </ContentBox>
    );
}
